/**
 * Dialog de captura fotográfica de documentos.
 *
 * Ciclo de estados internos:
 *   tutorial → requesting → previewing → captured
 *                       ↘ error (permiso denegado o sin cámara)
 *
 * - Muestra un tutorial específico al tipo de documento antes de activar la cámara.
 * - Prefiere cámara trasera (móvil); hace fallback a cualquier cámara disponible (laptop).
 * - Superpone un marco guía con la forma del documento esperado sobre el preview en vivo.
 * - Convierte el frame capturado a File (JPEG) y lo retorna vía onCapture.
 * - Detiene el MediaStream al cerrar o desmontar para liberar el hardware.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import CropFreeIcon from '@mui/icons-material/CropFree';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FlipIcon from '@mui/icons-material/Flip';
import HomeIcon from '@mui/icons-material/Home';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import LightModeIcon from '@mui/icons-material/LightMode';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ReplayIcon from '@mui/icons-material/Replay';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Variante de documento que determina el tutorial y la forma del marco guía.
 *   ine_front  — Anverso de la INE (credencial para votar)
 *   ine_back   — Reverso de la INE
 *   passport   — Página de datos del pasaporte (landscape, abierto plano)
 *   poa        — Comprobante de domicilio (portrait, proporciones carta/A4)
 */
export type DocVariant = 'ine_front' | 'ine_back' | 'passport' | 'poa';

type CaptureState = 'tutorial' | 'requesting' | 'previewing' | 'countdown' | 'captured' | 'error';

interface TutorialStep {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface DocVariantConfig {
  // Título que aparece en el encabezado del dialog durante el tutorial
  tutorialTitle: string;
  // Pasos del tutorial con ícono, título y descripción
  steps: TutorialStep[];
  // Forma del marco guía durante el preview de cámara.
  // 'landscape' usa width como ancla → el marco es más ancho que alto (INE/tarjeta).
  // 'portrait'  usa height como ancla → el marco es más alto que ancho (pasaporte/comprobante).
  frameOrientation: 'landscape' | 'portrait';
  // Proporción width/height del documento real.
  // INE (ID-1): 85.6mm × 53.98mm → 1.586  |  Pasaporte (ID-3 ICAO 9303): 125mm × 88mm → 1.42
  frameAspectRatio: number;
  // Ancho del marco como % del contenedor cuando frameOrientation='landscape'.
  // Alto del marco como % del contenedor cuando frameOrientation='portrait'.
  frameSizePercent: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Configuración por variante
// ─────────────────────────────────────────────────────────────────────────────

const VARIANT_CONFIG: Record<DocVariant, DocVariantConfig> = {
  ine_front: {
    tutorialTitle: 'Fotografiar frente de la INE',
    steps: [
      {
        icon: <BadgeIcon sx={{ fontSize: 28, color: 'primary.main' }} />,
        title: 'Cara frontal',
        description:
          'Coloca el frente de tu INE donde se ven tu fotografía, nombre y número de clave de elector.',
      },
      {
        icon: <LightModeIcon sx={{ fontSize: 28, color: 'warning.main' }} />,
        title: 'Buena iluminación',
        description:
          'Evita reflejos y sombras sobre la identificación. Aléjate de fuentes de luz directa.',
      },
      {
        icon: <CropFreeIcon sx={{ fontSize: 28, color: 'success.main' }} />,
        title: 'Documento completo',
        description:
          'Los 4 bordes de la INE deben quedar visibles dentro del marco guía. No cortes ningún extremo.',
      },
    ],
    frameOrientation: 'landscape',
    frameAspectRatio: 1.586,  // Formato ID-1: 85.6mm × 53.98mm
    frameSizePercent: 90,
  },
  ine_back: {
    tutorialTitle: 'Fotografiar reverso de la INE',
    steps: [
      {
        icon: <FlipIcon sx={{ fontSize: 28, color: 'primary.main' }} />,
        title: 'Voltea la identificación',
        description:
          'Da vuelta a tu INE para fotografiar el reverso donde están la dirección y el código de barras.',
      },
      {
        icon: <LightModeIcon sx={{ fontSize: 28, color: 'warning.main' }} />,
        title: 'Sin reflejos ni sombras',
        description:
          'Asegúrate de que el código de barras y los datos de dirección estén bien iluminados y sin brillo.',
      },
      {
        icon: <QrCodeIcon sx={{ fontSize: 28, color: 'success.main' }} />,
        title: 'Código de barras visible',
        description:
          'El código de barras del reverso debe quedar dentro del marco y ser legible. No lo cubras.',
      },
    ],
    frameOrientation: 'landscape',
    frameAspectRatio: 1.586,
    frameSizePercent: 90,
  },
  passport: {
    tutorialTitle: 'Fotografiar página de datos del pasaporte',
    steps: [
      {
        icon: <ImportContactsIcon sx={{ fontSize: 28, color: 'primary.main' }} />,
        title: 'Abre el pasaporte',
        description:
          'Abre tu pasaporte en la página con tu fotografía y número. Colócalo plano sobre una superficie.',
      },
      {
        icon: <LightModeIcon sx={{ fontSize: 28, color: 'warning.main' }} />,
        title: 'Evita reflejos en el plástico',
        description:
          'La laminación del pasaporte produce reflejos. Inclina ligeramente el documento para eliminarlos.',
      },
      {
        icon: <TextSnippetIcon sx={{ fontSize: 28, color: 'success.main' }} />,
        title: 'Zona MRZ legible',
        description:
          'Las dos líneas de caracteres al fondo de la página (zona de lectura mecánica) deben ser nítidas.',
      },
    ],
    frameOrientation: 'landscape',
    frameAspectRatio: 1.42,  // ID-3 ICAO 9303: 125mm × 88mm abierto plano (landscape)
    frameSizePercent: 90,
  },
  poa: {
    tutorialTitle: 'Fotografiar comprobante de domicilio',
    steps: [
      {
        icon: <DescriptionIcon sx={{ fontSize: 28, color: 'primary.main' }} />,
        title: 'Documento extendido y plano',
        description:
          'Extiende el comprobante sobre una superficie plana. Si está doblado, alísalo antes de fotografiar.',
      },
      {
        icon: <CalendarMonthIcon sx={{ fontSize: 28, color: 'warning.main' }} />,
        title: 'Fecha visible',
        description:
          'Asegúrate de que la fecha de emisión del comprobante sea claramente legible en la fotografía.',
      },
      {
        icon: <HomeIcon sx={{ fontSize: 28, color: 'success.main' }} />,
        title: 'Nombre y dirección completos',
        description:
          'Deben verse el nombre del titular, la dirección completa y el membrete o logo de la empresa emisora.',
      },
    ],
    frameOrientation: 'portrait',
    frameAspectRatio: 0.707,  // A4: 210mm × 297mm (portrait)
    frameSizePercent: 82,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const JPEG_QUALITY = 0.92;
const CAMERA_FILENAME = 'document-photo.jpg';
// Tiempo de espera (ms) entre que el usuario pulsa "Capturar" y la toma real del frame.
// Permite que el autofoco de la cámara se asiente tras la interacción táctil del usuario.
const CAPTURE_STABILIZATION_MS = 1000;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Solicita acceso a la cámara trasera (móvil) con fallback a la cámara disponible (laptop).
 *
 * Consulta la Permissions API antes de llamar getUserMedia para detectar si el permiso
 * está explícitamente denegado sin necesidad de esperar el timeout del navegador.
 * Si la API de permisos no está disponible (Firefox, Safari < 16), continúa directo
 * con getUserMedia — la verificación es best-effort.
 *
 * Estrategia de constraints:
 *   1. Intenta cámara trasera con resolución HD (ideal para captura de documentos).
 *   2. Si falla, hace fallback a cualquier cámara disponible sin restricciones.
 * El fallback cubre laptops (sin cámara trasera) y dispositivos donde el primer
 * conjunto de constraints no es soportado por el driver.
 */
async function requestCameraStream(): Promise<MediaStream> {
  // Verificar estado del permiso de cámara antes de llamar getUserMedia.
  // Permite dar instrucciones más precisas al usuario sin disparar el diálogo del browser.
  if (typeof navigator !== 'undefined' && navigator.permissions) {
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
      if (permissionStatus.state === 'denied') {
        // Lanzar con el mismo nombre de error que DOMException para que resolveErrorMessage lo maneje
        const err = new DOMException('Camera permission denied by browser', 'NotAllowedError');
        throw err;
      }
    } catch (permErr) {
      // Si el error ES el NotAllowedError que acabamos de lanzar, re-lanzarlo
      if ((permErr as { name?: string })?.name === 'NotAllowedError') {
        throw permErr;
      }
      // Si falla la Permissions API en sí (Firefox, Safari antiguo), continuar con getUserMedia
    }
  }

  // Intento 1: cámara trasera Full HD — más píxeles por mm para captura de documentos
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width:  { ideal: 1920 },
        height: { ideal: 1080 },
      },
    });
  } catch { /* continúa con el siguiente nivel */ }

  // Intento 2: cámara trasera HD — fallback para dispositivos que no alcanzan Full HD
  try {
    return await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width:  { ideal: 1280 },
        height: { ideal: 720 },
      },
    });
  } catch { /* continúa con el último fallback */ }

  // Intento 3: cualquier cámara disponible sin restricciones de resolución
  return navigator.mediaDevices.getUserMedia({ video: true });
}

/**
 * Traduce el nombre del error de getUserMedia a un mensaje legible en español.
 * Retorna un objeto con el mensaje principal y, para errores de permiso, una
 * instrucción adicional específica para Chrome móvil donde el cambio de permiso
 * en Configuración del sitio no toma efecto hasta recargar la página.
 */
function resolveErrorMessage(err: unknown): { message: string; hint: string | null } {
  const name = (err as { name?: string })?.name ?? '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return {
      message: 'Permiso de cámara denegado.',
      hint:
        'Si ya otorgaste el permiso en Configuración del navegador, cierra y vuelve a abrir esta página para que el cambio surta efecto. Luego pulsa "Reintentar".',
    };
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return {
      message: 'No se encontró ninguna cámara en este dispositivo.',
      hint: null,
    };
  }
  return {
    message: 'No se pudo acceder a la cámara. Intenta de nuevo o usa la opción de subir archivo.',
    hint: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

interface CameraCaptureProps {
  open: boolean;
  /** Llamado con el File resultante cuando el usuario confirma la foto. */
  onCapture: (file: File) => void;
  /** Llamado cuando el usuario cierra el dialog sin capturar. */
  onClose: () => void;
  /**
   * Instrucción contextual que se muestra bajo el preview de la cámara.
   * Ej.: "Coloca el anverso de tu INE dentro del marco"
   */
  label?: string;
  /**
   * Variante de documento. Cuando se provee, se muestra un tutorial específico
   * antes del preview de cámara y el marco guía adopta la forma del documento.
   */
  docVariant?: DocVariant;
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-vistas internas
// ─────────────────────────────────────────────────────────────────────────────

interface TutorialViewProps {
  config: DocVariantConfig;
  onStart: () => void;
}

/**
 * Pantalla de tutorial previa a la captura.
 * Muestra 3 consejos específicos al tipo de documento antes de activar la cámara.
 */
function TutorialView({ config, onStart }: TutorialViewProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body1" color="text.secondary">
        Sigue estos consejos para obtener una foto válida:
      </Typography>

      <Stack spacing={1.5}>
        {config.steps.map((step, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ flexShrink: 0, mt: 0.25 }}>{step.icon}</Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {step.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {step.description}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>

      <Button
        variant="contained"
        size="large"
        fullWidth
        startIcon={<CameraAltIcon />}
        onClick={onStart}
        sx={{ mt: 1 }}
      >
        Abrir cámara
      </Button>
    </Box>
  );
}

/**
 * Marco guía superpuesto al preview de cámara.
 * Para documentos en formato tarjeta (landscape) se ancla por ancho.
 * Para documentos en formato hoja (portrait) se ancla por alto.
 */
interface GuideFrameProps {
  config: DocVariantConfig;
}

function GuideFrame({ config }: GuideFrameProps) {
  const frameStyle =
    config.frameOrientation === 'landscape'
      ? {
          // Ancla por ancho — el alto se calcula automáticamente por aspectRatio
          width: `${config.frameSizePercent}%`,
          aspectRatio: String(config.frameAspectRatio),
        }
      : {
          // Ancla por alto — el ancho se calcula automáticamente por aspectRatio
          height: `${config.frameSizePercent}%`,
          aspectRatio: String(config.frameAspectRatio),
        };

  // Marcadores en L en las 4 esquinas — complementan el borde semitransparente y ayudan
  // al usuario a alinear el documento sin depender de buen contraste en el fondo.
  const corners = [
    { top: 0,    left:  0, borderTop:    '3px solid rgba(255,255,255,0.95)', borderLeft:   '3px solid rgba(255,255,255,0.95)' },
    { top: 0,    right: 0, borderTop:    '3px solid rgba(255,255,255,0.95)', borderRight:  '3px solid rgba(255,255,255,0.95)' },
    { bottom: 0, left:  0, borderBottom: '3px solid rgba(255,255,255,0.95)', borderLeft:   '3px solid rgba(255,255,255,0.95)' },
    { bottom: 0, right: 0, borderBottom: '3px solid rgba(255,255,255,0.95)', borderRight:  '3px solid rgba(255,255,255,0.95)' },
  ];

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        border: '1px solid rgba(255, 255, 255, 0.35)',
        borderRadius: 1,
        pointerEvents: 'none',
        ...frameStyle,
      }}
    >
      {corners.map((corner, idx) => (
        <Box key={idx} sx={{ position: 'absolute', width: 20, height: 20, ...corner }} />
      ))}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

export function CameraCapture({ open, onCapture, onClose, label, docVariant }: CameraCaptureProps) {
  // Si se provee docVariant, la primera pantalla es el tutorial; si no, va directo a la cámara
  const initialState: CaptureState = docVariant ? 'tutorial' : 'requesting';

  const [captureState, setCaptureState] = useState<CaptureState>(initialState);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorHint, setErrorHint] = useState<string | null>(null);
  const [capturedDataUrl, setCapturedDataUrl] = useState<string | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const variantConfig = docVariant ? VARIANT_CONFIG[docVariant] : null;

  const dialogTitle = variantConfig?.tutorialTitle
    ?? label
    ?? 'Fotografiar documento';

  /** Detiene todas las pistas del MediaStream activo. */
  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  /** Inicia el stream de cámara y ejecuta callbacks al éxito o error. */
  const startCamera = (onSuccess: () => void, onError: (err: unknown) => void) => {
    requestCameraStream()
      .then((stream) => {
        streamRef.current = stream;
        onSuccess();
      })
      .catch(onError);
  };

  // Conecta el stream al <video> DESPUÉS de que React lo haya montado en el DOM.
  // Cubre tanto 'previewing' como 'countdown': al transicionar entre ambos estados
  // React desmonta y vuelve a montar el <video>, por lo que hay que re-asignar srcObject.
  useEffect(() => {
    if ((captureState === 'previewing' || captureState === 'countdown') && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [captureState]);

  // Resetea el estado interno cada vez que el dialog abre/cierra.
  // Si hay docVariant, comienza en tutorial; si no, va directo a solicitar la cámara.
  useEffect(() => {
    if (!open) {
      stopStream();
      return;
    }

    let cancelled = false;
    const nextState: CaptureState = docVariant ? 'tutorial' : 'requesting';
    setCaptureState(nextState);
    setCapturedDataUrl(null);
    setCapturedFile(null);

    // Si no hay tutorial, iniciar la cámara de inmediato
    if (!docVariant) {
      startCamera(
        () => { if (!cancelled) setCaptureState('previewing'); },
        (err) => {
          if (cancelled) return;
          const { message, hint } = resolveErrorMessage(err);
          setErrorMessage(message);
          setErrorHint(hint);
          setCaptureState('error');
        },
      );
    }

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Llamado por TutorialView al pulsar "Abrir cámara" — arranca el stream. */
  const handleTutorialComplete = () => {
    setCaptureState('requesting');
    startCamera(
      () => setCaptureState('previewing'),
      (err) => {
        const { message, hint } = resolveErrorMessage(err);
        setErrorMessage(message);
        setErrorHint(hint);
        setCaptureState('error');
      },
    );
  };

  const handleClose = () => {
    stopStream();
    onClose();
  };

  /**
   * Toma el frame actual del video y lo convierte a File JPEG.
   * Recorta la imagen al área del marco guía — no captura el frame completo.
   * Se llama desde el useEffect de countdown — nunca directamente desde el botón.
   */
  const grabFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const containerW = video.clientWidth;
    const containerH = video.clientHeight;
    const videoW    = video.videoWidth;
    const videoH    = video.videoHeight;

    // Factor de escala de objectFit:'cover' — dimensión dominante llena el contenedor
    const scale   = Math.max(containerW / videoW, containerH / videoH);
    // Origen del frame escalado en coordenadas CSS (negativo = recortado por el contenedor)
    const offsetX = (containerW - videoW * scale) / 2;
    const offsetY = (containerH - videoH * scale) / 2;

    // Dimensiones del marco guía en píxeles CSS, centrado en el contenedor
    let frameCssW: number;
    let frameCssH: number;
    if (variantConfig) {
      if (variantConfig.frameOrientation === 'landscape') {
        frameCssW = containerW * (variantConfig.frameSizePercent / 100);
        frameCssH = frameCssW / variantConfig.frameAspectRatio;
      } else {
        frameCssH = containerH * (variantConfig.frameSizePercent / 100);
        frameCssW = frameCssH * variantConfig.frameAspectRatio;
      }
    } else {
      // Marco genérico: left 5%, right 5%, top 8%, bottom 8%
      frameCssW = containerW * 0.90;
      frameCssH = containerH * 0.84;
    }
    const frameCssX = (containerW - frameCssW) / 2;
    const frameCssY = (containerH - frameCssH) / 2;

    // Convertir coordenadas CSS del marco a píxeles del stream del sensor
    const srcX = (frameCssX - offsetX) / scale;
    const srcY = (frameCssY - offsetY) / scale;
    const srcW = frameCssW / scale;
    const srcH = frameCssH / scale;

    // Acotar a los límites del frame (seguridad ante redondeo o márgenes negativos)
    const cropX = Math.max(0, Math.round(srcX));
    const cropY = Math.max(0, Math.round(srcY));
    const cropW = Math.min(Math.round(srcW), videoW - cropX);
    const cropH = Math.min(Math.round(srcH), videoH - cropY);

    canvas.width  = cropW;
    canvas.height = cropH;
    canvas.getContext('2d')!.drawImage(video, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], CAMERA_FILENAME, { type: 'image/jpeg' });
        setCapturedDataUrl(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
        setCapturedFile(file);
        stopStream();
        setCaptureState('captured');
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  };

  /** Pulsar "Capturar" inicia el countdown — el frame se toma al terminar. */
  const handleCapture = () => {
    setCaptureState('countdown');
  };

  // Espera CAPTURE_STABILIZATION_MS para que el autofoco se asiente, luego toma el frame.
  useEffect(() => {
    if (captureState !== 'countdown') return;
    const timerId = setTimeout(grabFrame, CAPTURE_STABILIZATION_MS);
    return () => clearTimeout(timerId);
    // grabFrame usa refs internos — no necesita ser dependencia
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureState]);

  /** Reinicia el preview para que el usuario tome otra foto. */
  const handleRetry = () => {
    setCapturedDataUrl(null);
    setCapturedFile(null);
    setCaptureState('requesting');

    startCamera(
      () => setCaptureState('previewing'),
      (err) => {
        const { message, hint } = resolveErrorMessage(err);
        setErrorMessage(message);
        setErrorHint(hint);
        setCaptureState('error');
      },
    );
  };

  /** Reintenta el acceso a la cámara desde el estado de error — permite recuperarse
   * después de otorgar el permiso en Configuración del navegador sin cerrar el dialog. */
  const handleCameraRetry = () => {
    setErrorMessage('');
    setErrorHint(null);
    setCaptureState('requesting');

    startCamera(
      () => setCaptureState('previewing'),
      (err) => {
        const { message, hint } = resolveErrorMessage(err);
        setErrorMessage(message);
        setErrorHint(hint);
        setCaptureState('error');
      },
    );
  };

  const handleUsePhoto = () => {
    if (capturedFile) {
      stopStream();
      onCapture(capturedFile);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CameraAltIcon color="primary" />
          <Typography variant="h6" component="span">
            {dialogTitle}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {/* Tutorial específico al tipo de documento */}
        {captureState === 'tutorial' && variantConfig && (
          <TutorialView config={variantConfig} onStart={handleTutorialComplete} />
        )}

        {/* Solicitando acceso a cámara */}
        {captureState === 'requesting' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Preview en vivo con marco guía proporcional al documento */}
        {captureState === 'previewing' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {label ?? 'Coloca el documento dentro del marco y mantén el dispositivo estable.'}
            </Typography>
            <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', bgcolor: 'black' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', display: 'block', maxHeight: 320, objectFit: 'cover' }}
              />

              {/* Marco guía con la forma del documento */}
              {variantConfig ? (
                <GuideFrame config={variantConfig} />
              ) : (
                // Marco genérico cuando no se especificó variante
                <Box
                  sx={{
                    position: 'absolute',
                    top: '8%',
                    left: '5%',
                    right: '5%',
                    bottom: '8%',
                    border: '2px dashed rgba(255, 255, 255, 0.85)',
                    borderRadius: 2,
                    pointerEvents: 'none',
                  }}
                />
              )}

              <Typography
                variant="body2"
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 0,
                  right: 0,
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.95)',
                  textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                  px: 1,
                }}
              >
                Alinea el documento dentro del marco
              </Typography>
            </Box>
          </>
        )}

        {/* Countdown de estabilización — el stream sigue activo mientras el autofoco se asienta */}
        {captureState === 'countdown' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {label ?? 'Coloca el documento dentro del marco y mantén el dispositivo estable.'}
            </Typography>
            <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', bgcolor: 'black' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', display: 'block', maxHeight: 320, objectFit: 'cover' }}
              />
              {variantConfig && <GuideFrame config={variantConfig} />}
              {/* Overlay semitransparente con indicador de captura en curso */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  backgroundColor: 'rgba(0, 0, 0, 0.45)',
                }}
              >
                <CircularProgress size={48} thickness={4} sx={{ color: 'white' }} />
                <Typography
                  variant="body2"
                  sx={{ color: 'white', fontWeight: 600, textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}
                >
                  Estabilizando…
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Previsualización de la foto capturada */}
        {captureState === 'captured' && capturedDataUrl && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Revisa que el documento sea legible y que los bordes estén visibles. Si la foto no es clara, toma otra.
            </Typography>
            <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              <img
                src={capturedDataUrl}
                alt="Foto del documento capturada"
                style={{ width: '100%', display: 'block', maxHeight: 320, objectFit: 'contain' }}
              />
            </Box>
          </>
        )}

        {/* Error de acceso a cámara */}
        {captureState === 'error' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {errorMessage}
            </Typography>
            {errorHint && (
              <Typography variant="caption" color="text.disabled" textAlign="center" sx={{ px: 1 }}>
                {errorHint}
              </Typography>
            )}
          </Box>
        )}

        {/* Canvas oculto usado para extraer el frame del video */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        {captureState === 'previewing' && (
          <Button
            variant="contained"
            fullWidth
            startIcon={<CameraAltIcon />}
            onClick={handleCapture}
          >
            Capturar
          </Button>
        )}

        {captureState === 'captured' && (
          <>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={handleRetry}
              sx={{ flex: 1 }}
            >
              Reintentar
            </Button>
            <Button
              variant="contained"
              onClick={handleUsePhoto}
              sx={{ flex: 2 }}
            >
              Usar esta foto
            </Button>
          </>
        )}

        {captureState === 'error' && (
          <>
            <Button
              variant="contained"
              startIcon={<ReplayIcon />}
              onClick={handleCameraRetry}
              sx={{ flex: 2 }}
            >
              Reintentar cámara
            </Button>
            <Button variant="outlined" onClick={handleClose} sx={{ flex: 1 }}>
              Cerrar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
